import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { postJson } from "@/hooks/use-shares";
import { getJson } from "@/lib/api-client";
import { extractionKeys, projectKeys } from "@/lib/query";
import { extractionResponseSchema } from "@/schemas/extractions";

const url = (photoId: string) => `/api/extractions/${encodeURIComponent(photoId)}`;

export function useExtraction(photoId: string) {
  return useQuery({
    queryKey: extractionKeys.photo(photoId),
    queryFn: async () => (await getJson(url(photoId), extractionResponseSchema)).data,
  });
}

function useReview<Request>(path: "confirm" | "reject", success: string, failure: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ photoId, request }: { photoId: string; request: Request }) =>
      postJson(`${url(photoId)}/${path}`, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: extractionKeys.all });
      // A filter over make, model or serial may now match differently, but the list is what the
      // viewer stands on: refetching now would close it on the PM if the photo stopped matching.
      void queryClient.invalidateQueries({ queryKey: projectKeys.all, refetchType: "none" });
      toast.success(success);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : failure);
    },
  });
}

export function useConfirmExtraction() {
  return useReview<{ serial?: string; make?: string; model?: string; equipmentType?: string }>(
    "confirm",
    "Reading confirmed",
    "Could not confirm this",
  );
}

export function useRejectExtraction() {
  return useReview<Record<string, never>>("reject", "Reading rejected", "Could not reject this");
}
